import axios from ".";
import {
  BulletPointType,
  Job,
  JobStatus,
  JobTranslations,
} from "../pages/playground/types";

/**
 * This method hits `/multiagents/google-drive-files` API.
 * @returns Promise with gigs array
 */

export const getBatchesFolders = async ({
  folderId,
}: { folderId?: string } = {}) => {
  const url = folderId
    ? `/gdrive/batches-folders?folder_id=${folderId}`
    : `/gdrive/batches-folders`;
  return axios.get(url);
};

export const getStats = async ({ batchId }: { batchId: string }) => {
  return axios.get(`/batch/${batchId}/stats`);
};

export const getJobs = async ({
  batchId,
  page,
}: {
  batchId: string;
  page: number;
}) => {
  return axios.get(`/job?page=${page}&batch_id=${batchId}`);
};

export const startPreprocess = async ({ batchId }: { batchId: string }) => {
  return axios.post(`/multiagents/batch/${batchId}/preprocess`, {});
};

/*
export const getDetectedColor = async (batchId: string, masterId: string) => {
	return axios.post(`/multiagents/determine-color`, {
		batch_id: batchId,
		master_id: masterId,
	});
};

export const getDetectedCountryOfOrigin = async (
	batchId: string,
	masterId: string
) => {
	return axios.post(`/multiagents/determine-country-of-origin`, {
		batch_id: batchId,
		master_id: masterId,
	});
};

export const getDetectedGarmentType = async (
	batchId: string,
	folderId: string,
	masterId: string
) => {
	return axios.post(`/multiagents/determine-garment-type`, {
		batch_id: batchId,
		folder_id: folderId,
		master_id: masterId,
	});
};

export const getDetectedGender = async (
	batchId: string,
	folderId: string,
	masterId: string
) => {
	return axios.post(`/multiagents/determine-gender`, {
		batch_id: batchId,
		folder_id: folderId,
		master_id: masterId,
	});
};

export const getDetectedMaterial = async ({
	batchId,
	folderId,
	masterId,
}: {
	batchId: string;
	folderId: string;
	masterId: string;
}) => {
	return axios.post(`/multiagents/determine-material`, {
		batch_id: batchId,
		folder_id: folderId,
		master_id: masterId,
	});
};
*/

export interface CustomBulletPointMapping {
  column_name: string;
  custom_name: string;
  description?: string;
  mapped_to_type?: string;
}

export const confirmBatchConfiguration = async ({
  batchId,
  customBulletPoints = [],
}: {
  batchId: string;
  customBulletPoints?: CustomBulletPointMapping[];
}) => {
  return axios.post(`/batch/confirm-configuration/${batchId}`, {
    custom_bullet_points: customBulletPoints,
  });
};

export const generateBulletPointDescription = async ({
  bulletPointName,
  sampleValues,
}: {
  bulletPointName: string;
  sampleValues: string[];
}) => {
  return axios.post<{ data: { description: string } }>(
    `/batch/generate-bullet-point-description`,
    {
      bullet_point_name: bulletPointName,
      sample_values: sampleValues,
    }
  );
};

export const updateBrandGDriveFolder = async ({
  brandId,
  gdrive_folder_id,
}: {
  brandId: string;
  gdrive_folder_id: string;
}) => {
  return axios.patch(`/brand/${brandId}/gdrive-folder`, { gdrive_folder_id });
};

export const getBrandById = async ({ id }: { id: string }) => {
  return axios.get(`/brand/${id}`);
};

export const getBrands = async ({ page = 1 }: { page?: number }) => {
  return axios.get(`/brand?page=${page}`);
};

export const getBatchById = async (batchId: string) => {
  return axios.get(`/batch/${batchId}`);
};

export const getBatches = ({
  brand_id,
  page,
}: {
  brand_id?: string;
  page?: number;
}) => {
  return axios.get("/batch/", { params: { brand_id, page } });
};

export const getJobIds = async ({ batchId }: { batchId: string }) => {
  return axios.get(`/batch/${batchId}/job_ids`);
};

export const updateJobStatus = async ({
  id,
  status,
  feedback,
}: {
  id: string;
  status: JobStatus;
  feedback?: string;
}) => {
  return axios.patch(`/job/${id}/status`, { status, feedback });
};

export const bulkUpdateJobStatus = async ({
  job_ids,
  new_status,
}: {
  job_ids: string[];
  new_status: JobStatus;
}) => {
  return axios.post(`/job/bulk-status`, { job_ids, new_status });
};

export const updateJobClientFeedback = async ({
  id,
  feedback,
}: {
  id: string;
  feedback?: string;
}) => {
  return axios.patch(`/job/${id}/client-feedback`, { feedback });
};

export const getJobById = (jobId: string) => {
  return axios.get<{ data: Job }>(`/job/${jobId}`);
};

export const getJobTranslations = async (jobId: string) => {
  return axios.get<{ data: JobTranslations }>(`/job/${jobId}/translations`);
};

export const regenerateJobTranslations = async (
  jobId: string,
  body: { instructions: string[]; style: string | null }
) => {
  return axios.post<{ data: JobTranslations }>(
    `/job/${jobId}/regenerate-translations`,
    body
  );
};

export const getJobBulletPoints = async (jobId: string) => {
  return axios.get(`/bulletpoint?job_id=${jobId}`);
};

export const updateJobBulletPointsOrder = async ({
  jobId,
  bulletPointsOrder,
}: {
  jobId: string;
  bulletPointsOrder: string[];
}) => {
  return axios.patch(`/job/${jobId}/bullet-points-order`, {
    bullet_points_order: bulletPointsOrder,
  });
};

export const updateJobEditorialDescription = async ({
  jobId,
  editorialDescription,
}: {
  jobId: string;
  editorialDescription: string;
}) => {
  return axios.patch(`/job/${jobId}/editorial-description`, {
    editorial_description: editorialDescription,
  });
};

export const createJobEditorialDescription = async (jobId: string) => {
  return axios.post(`/job/${jobId}/editorial-description`);
};

export const setMainBulletPointVariant = async ({
  bulletpointId,
  variant,
}: {
  bulletpointId: string;
  variant: string;
}) => {
  return axios.patch(`/bulletpoint/${bulletpointId}/set-main-variant`, {
    variant,
    bulletpoint_id: bulletpointId,
  });
};

export const createBulletPoint = async ({
  bulletpoint_type,
  variants,
  job_id,
}: {
  bulletpoint_type: BulletPointType;
  variants: string[];
  job_id: string;
}) => {
  return axios.post(`/bulletpoint`, {
    bulletpoint_type,
    variants,
    job_id,
  });
};

export const createDetailBulletPoint = async ({
  job_id,
  variants,
}: {
  job_id: string;
  variants: string[];
}) => {
  return axios.post(`/bulletpoint/create-detail-bulletpoint`, {
    job_id,
    variants,
  });
};

export const createCustomBulletPoint = async ({
  job_id,
  variants,
  custom_name,
  custom_description,
}: {
  job_id: string;
  variants: string[];
  custom_name: string;
  custom_description?: string;
}) => {
  return axios.post(`/bulletpoint/create-custom-bulletpoint`, {
    job_id,
    variants,
    custom_name,
    custom_description,
  });
};

export const deleteBulletPoint = async ({
  bulletpointId,
}: {
  bulletpointId: string;
}) => {
  return axios.delete(`/bulletpoint/${bulletpointId}`);
};

export const addBulletPointVariant = async ({
  bulletpointId,
  variant,
}: {
  bulletpointId: string;
  variant: string;
}) => {
  return axios.patch(`/bulletpoint/${bulletpointId}/add-variant`, {
    variant,
    bulletpoint_id: bulletpointId,
  });
};

export const removeBulletPointVariant = async ({
  bulletpointId,
  variant,
}: {
  bulletpointId: string;
  variant: string;
}) => {
  return axios.patch(`/bulletpoint/${bulletpointId}/remove-variant`, {
    variant,
    bulletpoint_id: bulletpointId,
  });
};

export const clearBulletPointVariants = async ({
  bulletpointId,
}: {
  bulletpointId: string;
}) => {
  return axios.patch(`/bulletpoint/${bulletpointId}/clear-variants`);
};

export const getJobImages = async ({ jobId }: { jobId: string }) => {
  return axios.get(`/job/${jobId}/images`);
};

export const getJobThumbnails = async ({ jobId }: { jobId: string }) => {
  return axios.get(`/job/${jobId}/thumbnails`);
};

export const generateJobTitle = async (jobId: string) => {
  return axios.post(`/multiagents/determine-title`, { job_id: jobId });
};

export const cleanBatchSupportFiles = async ({
  batchId,
}: {
  batchId: string;
}) => {
  return axios.post(`/gdrive/${batchId}/batch-support-files-cleanup`, {});
};

export const syncMasterFile = async ({ batchId }: { batchId: string }) => {
  return axios.post(`/batch/master-file-sync/${batchId}`);
};

export const getJobNotes = async ({ jobId }: { jobId: string }) => {
  return axios.get(`/job/${jobId}/notes`);
};

export const updateJobNotes = async ({
  jobId,
  notes,
  client_notes,
}: {
  jobId: string;
  notes?: string;
  client_notes?: string;
}) => {
  return axios.patch(`/job/${jobId}/notes`, {
    notes,
    client_notes,
  });
};

export const createJobBackup = async ({ jobId }: { jobId: string }) => {
  return axios.post(`/job/backup`, { job_id: jobId });
};

export const getAdvancedStats = async ({ batchId }: { batchId: string }) => {
  return axios.get(`/batch/${batchId}/advanced-stats`);
};

/**
 * Export batch data with filtering options
 * @param batchId The batch ID to export
 * @param statuses Optional list of job statuses to include
 * @param columns Optional list of additional columns to include
 * @param format Export format: "csv" or "xlsx" (defaults to "csv")
 * @returns Promise with blob data for the file
 */
export const exportBatch = async ({
  batchId,
  statuses,
  columns,
  format = "csv",
}: {
  batchId: string;
  statuses: string[];
  columns: string[];
  format?: "csv" | "xlsx";
}) => {
  // Build the query parameters
  const params = new URLSearchParams();
  statuses.forEach((status) => params.append("statuses", status));
  columns.forEach((column) => params.append("columns", column));
  params.append("format", format);

  // Use axios with responseType blob for file download
  return axios.get(`/batch/export/${batchId}?${params.toString()}`, {
    responseType: "blob",
  });
};

// Backwards-compatible alias
export const exportBatchCsv = exportBatch;

/**
 * Tracks time spent on a job
 * @param jobId ID of the job being worked on
 * @param timeSpentSeconds Time spent in seconds
 * @param userId Optional user ID (uses authenticated user if not provided)
 * @returns Promise with the created time tracking record
 */
export const trackJobTime = async ({
  jobId,
  timeSpentSeconds,
}: {
  jobId: string;
  timeSpentSeconds: number;
}) => {
  return axios.post(`/job/track-time`, {
    job_id: jobId,
    time_spent: timeSpentSeconds,
  });
};

/**
 * Exports time tracking data as CSV
 * @param filters Optional filters for the export
 * @returns Promise with blob data for the CSV file
 */
export const exportTimeTracking = async ({
  brandId,
  batchId,
  startDate,
  endDate,
}: {
  brandId?: string;
  batchId?: string;
  startDate?: string; // ISO date format
  endDate?: string; // ISO date format
} = {}) => {
  // Build the query parameters
  const params = new URLSearchParams();
  if (brandId) params.append("brand_id", brandId);
  if (batchId) params.append("batch_id", batchId);
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);

  // Use axios with responseType blob for CSV file download
  return axios.get(`/time-tracking/export?${params.toString()}`, {
    responseType: "blob",
  });
};

/**
 * Get simple counters for client jobs that need attention
 * @returns Promise with to_check and to_rework counts
 */
export const getClientCounts = async () => {
  return axios.get<{ data: { to_check: number; to_rework: number } }>(
    "/batch/client-counts"
  );
};

/**
 * Unarchive a brand (restore from soft delete)
 * @param brandId ID of the brand to unarchive
 * @returns Promise with the unarchived brand data
 */
export const unarchiveBrand = async ({ brandId }: { brandId: string }) => {
  return axios.patch(`/brand/${brandId}/unarchive`);
};

/**
 * Unarchive a batch (restore from soft delete)
 * @param batchId ID of the batch to unarchive
 * @returns Promise with the unarchived batch data
 */
export const unarchiveBatch = async ({ batchId }: { batchId: string }) => {
  return axios.patch(`/batch/${batchId}/unarchive`);
};
